import { useQuery } from "@tanstack/react-query";
import { useDataProvider } from "ra-core";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import {
  MODULE_CATALOG,
  type ModuleSetupStep,
} from "@/modules/apps/moduleCatalog";
import type { ModuleId } from "@/modules/registry";

export type ModuleSetupStepStatus = ModuleSetupStep & {
  /** null: could not check (shown as to do). */
  done: boolean | null;
};

/** Live status of a module's setup steps for the member's company. */
export const useModuleSetup = (
  moduleId: ModuleId | null,
  options?: { enabled?: boolean },
) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const steps = moduleId ? MODULE_CATALOG[moduleId].setup : [];
  const query = useQuery({
    queryKey: ["module-setup", moduleId],
    queryFn: async (): Promise<ModuleSetupStepStatus[]> =>
      Promise.all(
        steps.map(async (step) => {
          try {
            return {
              ...step,
              done: await step.check({ supabase, dataProvider }),
            };
          } catch {
            return { ...step, done: null };
          }
        }),
      ),
    enabled: moduleId != null && steps.length > 0 && options?.enabled !== false,
    staleTime: 15_000,
  });

  const statuses = query.data ?? steps.map((step) => ({ ...step, done: null }));
  const required = statuses.filter((step) => !step.optional);
  const requiredDone = required.filter((step) => step.done === true).length;
  return {
    steps: statuses,
    isPending: query.isPending && steps.length > 0,
    requiredDone,
    requiredTotal: required.length,
    isReady: required.length === 0 || requiredDone === required.length,
    refetch: query.refetch,
  };
};
